import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  BINARY_DECOMPOSITION,
  FULL_SECURITY,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import {
  AutoIgnoreReasonDescriptions,
  Category,
  IssueFilterKeys,
  IssueScopeKind,
  VulnIgnoreReasonFilterDescriptions,
} from '../../../../shared/issuesV2';
import { PREMIUM_COMP_SEC_PLAN_NAME, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../../shared/subscriptions/consts';
import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useReleaseGroupRelease } from '../../../hooks/api/releaseGroups/useReleaseGroupRelease';

import './Filters.less';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import FeatureFlagRequired from '../../generic/Gates/FeatureFlagRequired';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import { useEnhancedScanFeaturesAvailable } from '../../projects/inventory/hooks/useEnhancedScanFeaturesAvailable';
import SavedFilters from '../../savedFilters';
import FossabotAlphaAd from '../../shared/ads/fossabotAlphaAd';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { EpssFilter, LicensingFilter, QualityFilter, VulnFilter } from '../../../../shared/issuesV2';
import type { ProjectType } from '../../../../typings/enums';

import ConfidenceIssueFilter from './ConfidenceIssueFilter';
import ContainerLayerFilter from './ContainerLayerFilter';
import DepthIssueFilter from './DepthIssueFilter';
import FoundAfterFilter from './FoundAfterFilter';
import IgnoreReasonFilter from './IgnoreReasonFilter';
import IssueSourceFilter from './IssueSourceFilter';
import IssueTypeFilter from './IssueTypeFilter';
import LicenseIdentificationFilter from './LicenseIdentificationFilter';
import LicenseIDFilter from './LicenseIDFilter';
import PackageManagerFilter from './PackageManagerFilter';
import ProjectLabelsFilter from './ProjectLabelsFilter';
import QualityIssueFilter from './QualityIssueFilter';
import TicketIssueFilter from './TicketIssueFilter';
import { CvssMetricFilter } from './VulnerabilityIssueFilter/CvssMetricFilter';
import CweFilter from './VulnerabilityIssueFilter/CweFilter';
import EPSSRangeFilter from './VulnerabilityIssueFilter/EPSSRangeFilter';
import ExploitMaturityFilter from './VulnerabilityIssueFilter/ExploitMaturityFilter';
import SeverityFilter from './VulnerabilityIssueFilter/SeverityFilter';
import UpgradeDistanceFilter from './VulnerabilityIssueFilter/UpgradeDistanceFilter';
import VulnTypeFilter from './VulnerabilityIssueFilter/VulnTypeFilter';

const IssuesFilters = () => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const projectType = useSelector(IssuesV2Store.selectors.getProjectType);
  const issueStatus = useSelector(IssuesV2Store.selectors.getStatus);
  const issueScope = useSelector(IssuesV2Store.selectors.getScope);
  const licenseList = useSelector(IssuesV2Store.selectors.getIssueLicenseList);

  const setFilterHasChanged = useSetFilterHasChanged();

  const isSecurityRestricted = !useIsFeatureEnabled(FULL_SECURITY);
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isVendoredDependencyEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const isReleaseGroupScope = issueScope.type === IssueScopeKind.ReleaseGroup;
  const { data: release } = useReleaseGroupRelease(
    isReleaseGroupScope ? Number(issueScope.id) : 0,
    isReleaseGroupScope ? issueScope.release : null,
  );
  const releaseProjectTypes = useMemo(() => {
    if (release) {
      return new Set<ProjectType>(release.projects.map(p => p.project.type));
    }

    return new Set<ProjectType>();
  }, [release]);

  // At the global scope, we do not show any filters that rely on project type
  const availableProjectTypes = useMemo(() => {
    if (issueScope.type === IssueScopeKind.Project) {
      if (projectType) {
        return new Set<ProjectType>([projectType]);
      }

      return new Set<ProjectType>();
    }

    if (issueScope.type === IssueScopeKind.ReleaseGroup) {
      return releaseProjectTypes;
    }

    return new Set<ProjectType>();
  }, [issueScope, projectType, releaseProjectTypes]);

  const {
    isBinaryAnalysisAvailable,
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
  } = useEnhancedScanFeaturesAvailable(availableProjectTypes);

  let shouldShowSnippetFilter = areSnippetsAvailable;
  let shouldShowVendoredFilter = areVendoredDependenciesAvailable;

  if (issueScope.type === IssueScopeKind.Global) {
    shouldShowSnippetFilter = isSnippetScanningEnabled;
    shouldShowVendoredFilter = isVendoredDependencyEnabled;
  }

  const canShowIssueSourceFilter = shouldShowSnippetFilter || shouldShowVendoredFilter;

  // utility function for selecting items in Checkbox filter group
  const onSelect = (filterKey: IssueFilterKeys) => (key: string, selected: boolean) => {
    const existingOptions = (
      filterKey in stateFilters ? stateFilters[filterKey as keyof typeof stateFilters] : []
    ) as string[];

    let newOptions: string[];

    if (selected) {
      // If the filter option (key) was selected, a new array is created including this option.
      newOptions = [...existingOptions, key];
    } else {
      // Otherwise, a new array is created without the unselected filter option (key)
      newOptions = (existingOptions).filter((selectedOption: string) => selectedOption !== key);
    }
    setFilters({ ...stateFilters, [filterKey]: newOptions });
    setFilterHasChanged(true);
  };

  // utility function for selecting items in MultiSelect filter group.
  const onSet = (filterKey: IssueFilterKeys) => (selectedValues: string[]) => {
    setFilters({ ...stateFilters, [filterKey]: selectedValues });
    setFilterHasChanged(true);
  };

  //  For setting the EPSS filter, which is a Range selector + a radio button to select the type of EPSS.
  //  If `null` is passed, it means the EPSS filter is being removed.
  const onSetEPSS = (epssFilter: EpssFilter | null) => {
    if (epssFilter === null) {
      return setFilters({
        ...stateFilters,
        [IssueFilterKeys.Epss]: undefined,
      });
    }
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Epss]: epssFilter,
    });
    return setFilterHasChanged(true);
  };

  const getFiltersForProjectType = () => {
    switch (projectType) {
      case 'container':
        return (
          <ContainerLayerFilter
            onSelect={onSelect(IssueFilterKeys.ContainerLayers)}
            selectedOptions={stateFilters[IssueFilterKeys.ContainerLayers]}
          />
        );
      default:
        return null;
    }
  };

  const getFiltersForCurrentCategory = () => {
    switch (issueCategory) {
      case Category.Licensing:
        return (
          <>
            {issueStatus === 'ignored' && (
              <IgnoreReasonFilter
                descriptions={AutoIgnoreReasonDescriptions}
                testId="issues-licensing-ignore-reason"
                onSet={onSet(IssueFilterKeys.IgnoreReason)}
                selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.IgnoreReason]}
              />
            )}
            <IssueTypeFilter
              onSelect={onSelect(IssueFilterKeys.Type)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Type]}
            />
            <LicenseIdentificationFilter
              onSelect={onSelect(IssueFilterKeys.Identification)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Identification]}
            />
            <LicenseIDFilter
              onSet={onSet(IssueFilterKeys.Licenses)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Licenses] || []}
              licenses={licenseList}
            />
            { canShowIssueSourceFilter && (
              <IssueSourceFilter
                selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.IssueSource]}
                shouldShowSnippetFilter={shouldShowSnippetFilter}
                shouldShowVendoredFilter={shouldShowVendoredFilter}
              />
            ) }
          </>
        );

      case Category.Vulnerability:
        return (
          <>
            {issueStatus === 'ignored' && (
              <IgnoreReasonFilter
                descriptions={VulnIgnoreReasonFilterDescriptions}
                testId="issues-vuln-ignore-reason"
                onSet={onSet(IssueFilterKeys.IgnoreReason)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.IgnoreReason]}
              />
            )}
            <VulnTypeFilter
              selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.Type]}
            />
            <CweFilter />
            <SeverityFilter
              onSelect={onSelect(IssueFilterKeys.Severity)}
              selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.Severity]}
              selectedSeveritySourceOptions={(stateFilters as VulnFilter)[IssueFilterKeys.SeveritySource]}
            />
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <UpgradeDistanceFilter
                onSelect={onSelect(IssueFilterKeys.UpgradeDistance)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.UpgradeDistance]}
              />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <ExploitMaturityFilter
                onSelect={onSelect(IssueFilterKeys.ExploitMaturity)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.ExploitMaturity]}
              />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <EPSSRangeFilter
                filter={(stateFilters as VulnFilter)[IssueFilterKeys.Epss]}
                onChange={onSetEPSS}
              />
            </PaidFeatureGate>
            { canShowIssueSourceFilter && (
              <IssueSourceFilter
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.IssueSource]}
                shouldShowSnippetFilter={shouldShowSnippetFilter}
                shouldShowVendoredFilter={shouldShowVendoredFilter}
              />
            ) }
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <CvssMetricFilter metric="attackVector" />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <CvssMetricFilter metric="attackComplexity" />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <CvssMetricFilter metric="privilegesRequired" />
            </PaidFeatureGate>
          </>
        );

      case Category.Quality:
        return (
          <>
            <QualityIssueFilter
              // @ts-expect-error FIXME: No overload matches this call.
              onSelect={onSelect(IssueFilterKeys.Type)}
              selectedOptions={(stateFilters as QualityFilter)[IssueFilterKeys.Type]}
            />
            {shouldShowVendoredFilter && (
              <IssueSourceFilter
                selectedOptions={(stateFilters as QualityFilter)[IssueFilterKeys.IssueSource]}
                shouldShowSnippetFilter={false} // Quality Issues are not available on snippets
                shouldShowVendoredFilter={shouldShowVendoredFilter}
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="issues-filter">
      <SavedFilters />
      {issueCategory === Category.Vulnerability && (
        <FossabotAlphaAd />
      )}
      <PackageManagerFilter />
      {issueScope.type === IssueScopeKind.Global && <ProjectLabelsFilter />}
      <DepthIssueFilter
        onSelect={onSelect(IssueFilterKeys.Depths)}
        selectedOptions={stateFilters[IssueFilterKeys.Depths]}
      />
      {isBinaryAnalysisAvailable && (
        <FeatureFlagRequired flagName={BINARY_DECOMPOSITION}>
          <ConfidenceIssueFilter
            onSelect={onSelect(IssueFilterKeys.Confidence)}
            selectedOptions={stateFilters[IssueFilterKeys.Confidence]}
          />
        </FeatureFlagRequired>
      )}
      <PaidFeatureGate
        planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}
        isRestricted={isFreeTier({
          plan: window.user?.organization?.stripe_plan,
          stripePriceLimit: window.user?.organization?.stripePriceLimit,
        })}
      >
        <TicketIssueFilter selectedOptions={stateFilters[IssueFilterKeys.Ticketed]} />
      </PaidFeatureGate>
      <PaidFeatureGate
        planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}
        isRestricted={isFreeTier({
          plan: window.user?.organization?.stripe_plan,
          stripePriceLimit: window.user?.organization?.stripePriceLimit,
        })}
      >
        <FoundAfterFilter />
      </PaidFeatureGate>

      {getFiltersForProjectType()}
      {getFiltersForCurrentCategory()}
    </div>
  );
};

export default IssuesFilters;
